import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { extractCvIdsFromFieldValue } from '../cleanStr';
import {
  ActionBuilderTemplate,
  hubspotContactCreationTemplateInitialValue,
  shopifyTemplateInitialValue,
} from '../constants';
import { ActionBuilderActionTypes, Routes } from '../enums';
import { hasDynamicListChildren } from '../solve/dynamicContextVariableUtils';
import capitalize from 'lodash/fp/capitalize';
import flow from 'lodash/fp/flow';
import isEmpty from 'lodash/fp/isEmpty';
import mapKeys from 'lodash/fp/mapKeys';
import mapValues from 'lodash/fp/mapValues';
import pickBy from 'lodash/fp/pickBy';
import {
  setSelectedActionTemplate,
  setSelectedInitialActionName,
} from 'src/actions/action-builder-actions/actionBuilderActions';
import {
  ALLOWED_DISPLAY_NAME_CHARACTER_REGEX,
  ALLOWED_DISPLAY_NAME_LENGTH,
} from 'src/pages/action-builder/context-variables-settings/constants';
import {
  excludeEmbedScriptParamNames,
  includeEmbedScriptParamNames,
} from 'src/reducers/actionBuilderReducer/constants';
import { AppDispatch } from 'src/store/hooks';
import {
  Action,
  AddOrUpdateActionInterface,
  areActionBuilderFields,
  areShopifyFields,
  areSnowflakeFields,
  AuthorizationConfig,
  AuthorizationField,
  BrowserAgentOutputContextVariables,
  BrowserAgentSecret,
  ContentType,
  ContextVariable,
  DynamicListKVPairs,
  DynamicListOption,
  isApiActionFields,
  isBrowserAgentActionFields,
  KeyValuePairsDict,
  KVPairs,
  Method,
  OutputValues,
  ProxyTestRequest,
  SelectedActionDetail,
  SelectedActionTemplate,
} from 'src/types/actionBuilderApiTypes';
import { isDynamicListType } from 'src/utils/solve/dynamicContextVariableUtils';

export const CLOUD_FLARE_PROXY_URL = `https://cors-proxy-v2.forethought-ai.workers.dev/proxy?auth_token=${CORS_PROXY_AUTH_TOKEN}`;

export const EMPTY_KEY_VALUE = [{ key: '', value: undefined }];
export const newActionStringPrefix = 'create?newActionId=';
export const generateNewActionString = () =>
  `${newActionStringPrefix}${uuidv4()}`;
export const generateNewActionStringWithActionId = (actionId: string) =>
  `${newActionStringPrefix}${actionId}`;

export const replaceActionIdInRoute = (newActionId: string) => {
  // Split tabId into path part and query params
  // e.g., "create?newActionId=xyz&type=browser_agent" -> path: "create", search: "?newActionId=xyz&type=browser_agent"
  const [pathPart, ...queryParts] = newActionId.split('?');
  const queryString = queryParts.length > 0 ? `?${queryParts.join('?')}` : '';

  return (
    Routes.ACTION_BUILDER_EDIT_V2.replace(':actionId?', pathPart) + queryString
  );
};

export const generateSelectedMcpActionURL = (actionId: string) =>
  `${Routes.ACTION_BUILDER}?tab=mcp&selected-action-id=${actionId}`;

export const generateSelectedZendeskActionURL = (actionId: string) =>
  `${Routes.ACTION_BUILDER}?tab=actions&selected-action-id=${actionId}`;

// Action types that can reference context variables (so they surface as CV
// "action usages") but have no standalone editor in the action builder — they're
// authored inline inside a workflow step on the canvas. Deeplinking them to the
// action editor would land on an endless spinner, so they resolve to no href
// (and the CV drawer drops them, since the containing agent already represents
// the usage). Verified absent from the action-builder editor pages.
const ACTION_TYPES_WITHOUT_BUILDER_EDITOR = new Set<string>([
  ActionBuilderActionTypes.CODE_EXECUTION,
  ActionBuilderActionTypes.SET_CONTEXT_VARIABLE,
  ActionBuilderActionTypes.CONTEXT_VARIABLE_MAPPING,
  ActionBuilderActionTypes.PARSE_JWT,
  ActionBuilderActionTypes.DYNAMIC_CARD,
]);

/**
 * Build a deeplink to where an action is edited, routed by action type:
 *   - MCP / Zendesk flowstate actions live in their own action-builder tab
 *     (selected via a query param), not the standalone editor route.
 *   - Inline-only step types (see {@link ACTION_TYPES_WITHOUT_BUILDER_EDITOR})
 *     have no action-builder editor -> no deeplink (`null`).
 *   - Everything else opens the standalone action editor.
 *
 * Returns `null` when there's no resolvable/working destination (unknown type or
 * missing id), so callers can render a non-clickable entry rather than a broken
 * link.
 */
export const getActionEditorUrl = (
  actionId: string,
  actionType: string | undefined,
): string | null => {
  if (!actionId || !actionType) {
    return null;
  }
  if (actionType === ActionBuilderActionTypes.MCP) {
    return generateSelectedMcpActionURL(actionId);
  }
  if (actionType === ActionBuilderActionTypes.ZENDESK_FLOWSTATE_ACTION) {
    return generateSelectedZendeskActionURL(actionId);
  }
  if (ACTION_TYPES_WITHOUT_BUILDER_EDITOR.has(actionType)) {
    return null;
  }
  return replaceActionIdInRoute(actionId);
};

const emptySelectedActionDetail: SelectedActionDetail = {
  description: '',
  fields: {
    authorization: null,
    body: null,
    headers: EMPTY_KEY_VALUE,
    jmespathQuery: '',
    method: 'GET',
    params: EMPTY_KEY_VALUE,
    url: '',
  },
  name: '',
  outputValues: [],
  tabId: '',
};

const emptySelectedBrowserAgentActionDetail: SelectedActionDetail = {
  description: '',
  fields: {
    policy: '',
  },
  name: '',
  outputValues: [],
  tabId: '',
};

const normalizeBrowserAgentOutputContextVariables = (
  outputContextVariables?: BrowserAgentOutputContextVariables,
): Record<string, string> => {
  // Normalizes browser agent output_context_variables API Response
  // returns the output_context_variables with a parent kv_list
  if (!outputContextVariables || typeof outputContextVariables !== 'object') {
    return {};
  }

  const kvList = (
    outputContextVariables as {
      kv_list?: { key?: string; value?: string }[];
    }
  ).kv_list;

  if (Array.isArray(kvList)) {
    const normalized: Record<string, string> = {};
    kvList.forEach(item => {
      if (typeof item?.key === 'string' && typeof item?.value === 'string') {
        normalized[item.key] = item.value;
      }
    });
    return normalized;
  }

  const normalized: Record<string, string> = {};
  Object.entries(outputContextVariables).forEach(([key, value]) => {
    if (key === 'kv_list' || typeof value !== 'string') {
      return;
    }
    normalized[key] = value;
  });
  return normalized;
};

export const actionToSelectedActionDetail = ({
  action,
  actionType,
  contextVariables,
  secrets = [],
  tabId = '',
}: {
  action?: Action;
  actionType?: string;
  contextVariables?: ContextVariable[];
  secrets?: BrowserAgentSecret[];
  tabId?: string;
} = {}): SelectedActionDetail => {
  if (action && isBrowserAgentActionFields(action.action_fields)) {
    const output_context_variables =
      normalizeBrowserAgentOutputContextVariables(
        action.action_fields.output_context_variables,
      );
    return {
      actionId: action.action_id,
      description: action.action_description,
      fields: {
        domains: action.action_fields.domains || [],
        initial_url: action.action_fields.initial_url,
        loading_texts: action.action_fields.loading_texts || [],
        output_context_variables,
        policy: action.action_fields.policy,
        secrets,
        user_agent: action.action_fields.user_agent || null,
      },
      is_background_task: action.is_background_task,
      name: action.action_name,
      outputValues: [],
      tabId,
    };
  }

  if (action && areActionBuilderFields(action.action_fields)) {
    return {
      actionId: action.action_id,
      description: action.action_description,
      fields: {
        authorization: getAuthTypeFromFields(action),
        body: action.action_fields.body_parameters ?? null,
        headers: isEmpty(action.action_fields.headers)
          ? EMPTY_KEY_VALUE
          : Object.entries(action.action_fields.headers).map(
              ([key, value]) => ({ key, value }),
            ),
        jmespathQuery: action.action_fields.jmespath_query || '',
        method: action.action_fields.method.toUpperCase() as Method,
        params: isEmpty(action.action_fields.query_parameters)
          ? EMPTY_KEY_VALUE
          : Object.entries(action.action_fields.query_parameters ?? {}).map(
              ([key, value]) => ({ key, value }),
            ),
        url: action.action_fields.url,
      },
      is_background_task: action.is_background_task,
      name: action.action_name,
      outputValues: Object.entries(
        action.action_fields.output_context_variables ?? {},
      ).map(([key, value]) => {
        const relevantCV = contextVariables?.find(
          cv => cv.context_variable_id === value,
        );

        if (isDynamicListType(relevantCV?.context_variable_type)) {
          return {
            jsonSchema:
              relevantCV.configuration_fields?.dynamic_list_config?.json_schema,
            key,
            newContextVariableName: relevantCV.context_variable_name,
            newContextVariableType:
              relevantCV.context_variable_type === 'DYNAMIC_LIST_MULTI_SELECT'
                ? 'DYNAMIC_LIST_MULTI_SELECT'
                : 'DYNAMIC_LIST',
            value,
          };
        }

        return { key, value };
      }),
      tabId,
    };
  }

  if (actionType === ActionBuilderActionTypes.BROWSER_AGENT) {
    return { ...emptySelectedBrowserAgentActionDetail, tabId };
  }

  return { ...emptySelectedActionDetail, tabId };
};

const getAuthTypeFromFields = ({
  action_fields: actionFields,
}: Action): AuthorizationField => {
  if (!areActionBuilderFields(actionFields)) {
    return null;
  }
  if (
    actionFields.authorization_type === 'oauth_client_credentials' ||
    actionFields.oauth_connector_id
  ) {
    return {
      id: actionFields.oauth_connector_id ?? '',
      scope: actionFields.oauth_scope ?? '',
      type: 'oauth_client_credentials',
    };
  }
  if (actionFields.authorization_config_id) {
    return { id: actionFields.authorization_config_id, type: 'bearer' };
  }
  if (actionFields.authorization_integration) {
    return {
      connectorId: actionFields.connector_id || '',
      id: actionFields.authorization_integration,
      type: 'integration',
    };
  }
  if (actionFields.authorization_conversation_oauth_client_id) {
    return {
      id: actionFields.authorization_conversation_oauth_client_id,
      type: 'authorization_client',
    };
  }

  return null;
};

export const nameKVDictValues = (pairs: KVPairs[]) =>
  pairs
    .filter(pair => pair.key && pair.value)
    .map(pair => ({ name: pair.key, value: pair.value ?? '' }));

export const selectedActionDetailToAction = (
  selectedAction: SelectedActionDetail,
  secrets: BrowserAgentSecret[] = [],
): AddOrUpdateActionInterface => {
  // Handle browser_agent action type
  if (isBrowserAgentActionFields(selectedAction.fields)) {
    const outputContextVariables = normalizeBrowserAgentOutputContextVariables(
      selectedAction.fields.output_context_variables,
    );
    return {
      action_description: selectedAction.description,
      action_fields: {
        domains: selectedAction.fields.domains || [],
        initial_url: selectedAction.fields.initial_url,
        loading_texts: selectedAction.fields.loading_texts || [],
        output_context_variables: outputContextVariables,
        policy: selectedAction.fields.policy,
        secrets,
        user_agent: selectedAction.fields.user_agent || null,
      },
      action_name: selectedAction.name,
      action_type: ActionBuilderActionTypes.BROWSER_AGENT,
      is_background_task: selectedAction.is_background_task,
    };
  }

  const fields = selectedAction.fields;

  const authorizationConfigId =
    fields.authorization?.type === 'bearer' ? fields.authorization?.id : null;

  const authorizationIntegration =
    fields.authorization?.type === 'integration'
      ? fields.authorization?.id
      : null;

  const connectorId =
    fields.authorization?.type === 'integration'
      ? fields.authorization?.connectorId
      : null;

  const headers = Object.fromEntries(
    (fields.headers ?? [])
      .filter(item => item.key && item.value)
      .map(({ key, value }) => [key, value ?? '']),
  );

  const params = Object.fromEntries(
    (fields.params ?? [])
      .filter(item => item.key && item.value)
      .map(({ key, value }) => [key, value ?? '']),
  );

  const authorizationConversationOauthClientId =
    fields.authorization?.type === 'authorization_client'
      ? fields.authorization?.id
      : null;

  const isOauthClientCredentials =
    fields.authorization?.type === 'oauth_client_credentials';
  const oauthConnectorId = isOauthClientCredentials
    ? fields.authorization?.id || null
    : null;
  const oauthScope = isOauthClientCredentials
    ? fields.authorization?.scope || null
    : null;
  const authorizationType = isOauthClientCredentials
    ? 'oauth_client_credentials'
    : null;

  return {
    action_description: selectedAction.description,
    action_fields: {
      authorization_config_id: authorizationConfigId,
      authorization_conversation_oauth_client_id:
        authorizationConversationOauthClientId,
      authorization_integration: authorizationIntegration,
      authorization_type: authorizationType,
      body_parameters: fields.body,
      connector_id: connectorId,
      headers: isEmpty(headers) ? null : headers,
      jmespath_query: fields.jmespathQuery,
      method: fields.method.toLowerCase(),
      natural_language_query: fields.jmespathQuery,
      oauth_connector_id: oauthConnectorId,
      oauth_scope: oauthScope,
      output_context_variables: selectedAction.outputValues?.length
        ? Object.fromEntries(
            selectedAction.outputValues.map(({ key, value }) => [
              key,
              value ?? '',
            ]),
          )
        : undefined,
      query_parameters: isEmpty(params) ? null : params,
      url: fields.url,
    },
    action_name: selectedAction.name,
    action_type: 'api',
    is_background_task: selectedAction.is_background_task,
  };
};

const validateKV = Yup.array().test(arr => {
  if (!arr) {
    return true;
  }

  if (arr.length === 1 && !arr[0].key && !arr[0].value) {
    return true;
  }

  return arr.every(({ key, value }) => key?.trim() && value?.trim());
});

const validateUniqueKey = (arr: Partial<KVPairs>[] | undefined) => {
  return new Set(arr?.map(item => item?.key)).size === arr?.length;
};

const validateUniqueActionName = (actions: Action[], actionName?: string) => {
  return !new Set(actions.map(item => item.action_name)).has(actionName ?? '');
};

const isSingleContextVariable = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed.startsWith('{{') && trimmed.endsWith('}}');
};

export const buildActionValidationSchema = (
  responseData: unknown,
  actions: Action[] = [],
  contextVariables: Readonly<ContextVariable[]> = [],
  jmespathQueryTestCvs: Partial<Record<string, string>>,
  currentSelectedAction?: Action,
  isBrowserAgentAction?: boolean,
) => {
  // Shared validation for name and description
  const baseValidation = {
    description: Yup.string()
      .trim()
      .min(1)
      .required('Action description is required'),
    name: Yup.string()
      .trim()
      .min(1)
      .required('Action name is required')
      .matches(
        /^[a-zA-Z0-9_-\s]{1,64}$/,
        'Action name cannot contain special characters',
      )
      .test('uniqueActionName', 'Action names must be unique', name =>
        name === currentSelectedAction?.action_name
          ? true
          : validateUniqueActionName(actions, name),
      ),
  };

  // Browser Agent actions have policy validation
  if (isBrowserAgentAction) {
    return Yup.object().shape({
      ...baseValidation,
      fields: Yup.object().shape({
        initial_url: Yup.string()
          .required('Initial URL is required')
          .test(
            'isValidUrl',
            'Please follow the correct URL format (e.g. https://example.com)',
            value => {
              if (!value) {
                return false;
              }
              // When wildcard exists ignore URL validation
              if (value.includes('*')) {
                return true;
              }
              // When CVs exist ignore URL validation
              const hasContextVariables = /\{\{.*?\}\}|\$\w+/.test(value);
              if (hasContextVariables) {
                return true;
              }

              return Yup.string().url().isValidSync(value);
            },
          ),
        policy: Yup.string()
          .required('Policy is required')
          .test('hasContent', 'Policy is required', value => {
            if (!value) {
              return false;
            }
            // Remove HTML tags and check if there's actual content
            const textContent = value.replace(/<[^>]*>/g, '').trim();
            return textContent.length > 0;
          }),
        // user_agent: no validation
      }),
    });
  }

  // API actions have full validation
  return Yup.object().shape({
    ...baseValidation,
    fields: Yup.object().shape({
      authorization: Yup.object()
        .nullable()
        .shape({
          id: Yup.string().when('type', value => {
            if (value) {
              return Yup.string().required();
            }
            return Yup.string().nullable();
          }),
          scope: Yup.string().nullable(),
          type: Yup.string().nullable(),
        }),
      body: Yup.string()
        .when('method', {
          is: 'GET',
          otherwise: Yup.string()
            .nullable()
            .when('headers', {
              is: (headers: KVPairs[]) =>
                getContentTypeFromHeaders(headers) === 'json',
              then: Yup.string()
                .nullable()
                .required('Invalid JSON')
                .test('json', 'Invalid JSON', value => {
                  if (!value) {
                    return false;
                  }

                  if (isSingleContextVariable(value)) {
                    return true;
                  }

                  // null parses in quoted and unquoted positions, so CVs of any type -- including nested dynamic-list children -- may be written without surrounding quotes.
                  try {
                    JSON.parse(value.replaceAll(/{{.*?}}/g, 'null'));
                  } catch {
                    return false;
                  }
                  return true;
                }),
            })
            .when('headers', {
              is: (headers: KVPairs[]) =>
                getContentTypeFromHeaders(headers) === 'form-url',
              then: Yup.string()
                .nullable()
                .required('Invalid format')
                .test('formUrlSpaces', 'Invalid format: no spaces', value => {
                  return !value?.includes(' ');
                })
                .test(
                  'formUrlEqualsSigns',
                  'Invalid format: missing equal sign(s)',
                  value => {
                    const keyValueArr = value?.split('&') ?? [];
                    for (const kv of keyValueArr) {
                      if (!kv.includes('=')) {
                        return false;
                      }
                    }
                    return true;
                  },
                ),
            }),
          then: Yup.string().nullable().notRequired(),
        })
        .nullable(),
      headers: validateKV.test(validateUniqueKey),
      jmespathQuery: Yup.string().trim().min(1),
      params: validateKV.test(validateUniqueKey),
      url: Yup.string()
        .trim()
        .min(1)
        .required('Url is invalid')
        .test('url', 'Url is invalid', url => {
          if (!url) {
            return false;
          }
          if (isSingleContextVariable(url)) {
            return true;
          }
          try {
            new URL(url);
          } catch {
            return false;
          }
          return true;
        }),
    }),
    name: Yup.string()
      .trim()
      .min(1)
      .required('Action name is required')
      .matches(
        /^[a-zA-Z0-9_-\s]{1,64}$/,
        'Action name cannot contain special characters',
      )
      .test('uniqueActionName', 'Action names must be unique', name =>
        name === currentSelectedAction?.action_name
          ? true
          : validateUniqueActionName(actions, name),
      ),
    outputValues: Yup.array()
      .of(
        Yup.object().shape({
          jsonSchema: Yup.object(),
          key: Yup.string().when('jsonSchema', jsonSchema => {
            if (!jsonSchema) {
              return Yup.string().trim().min(1).required();
            }
            return Yup.string().trim().min(0).notRequired();
          }),
          newContextVariableName: Yup.string().when(
            'jsonSchema',
            jsonSchema => {
              if (!jsonSchema) {
                return Yup.string().nullable().notRequired();
              }
              return Yup.string()
                .trim()
                .min(1, 'Give the dynamic list a name')
                .max(
                  ALLOWED_DISPLAY_NAME_LENGTH,
                  `Names must be ${ALLOWED_DISPLAY_NAME_LENGTH} characters or less`,
                )
                .required('Give the dynamic list a name')
                .test(
                  'Matches regex',
                  'Display names must not use special characters',
                  value => {
                    return !ALLOWED_DISPLAY_NAME_CHARACTER_REGEX.test(
                      value ?? '',
                    );
                  },
                )
                .test(
                  'uniqueDynamicCVName',
                  'Context variable names must be unique',
                  function (name) {
                    const outputValues:
                      | { newContextVariableName: string }[]
                      | undefined = this.options.context?.outputValues;

                    const hasDuplicateInCurrentList =
                      outputValues &&
                      outputValues.filter(
                        outputValue =>
                          outputValue.newContextVariableName === name,
                      ).length > 1;

                    return !hasDuplicateInCurrentList;
                  },
                );
            },
          ),
          value: Yup.string().when('jsonSchema', jsonSchema => {
            if (!jsonSchema) {
              return Yup.string()
                .trim()
                .min(1)
                .test(v => {
                  return contextVariables.some(
                    cv => cv.context_variable_id === v,
                  );
                })
                .required();
            }
            return Yup.string().trim().min(0).notRequired();
          }),
        }),
      )
      .test(validateUniqueKey),
  });
};

export const getContentTypeFromHeaders = (
  headers: Partial<KVPairs>[] | undefined,
): ContentType => {
  const contentTypeHeader = headers?.find(
    header => header.key?.toLowerCase() === 'content-type',
  );

  if (!contentTypeHeader?.value) {
    return 'none';
  }

  const value = contentTypeHeader.value.toLowerCase();

  if (value.includes('application/json')) {
    return 'json';
  }

  if (value.includes('application/x-www-form-urlencoded')) {
    return 'form-url';
  }

  return 'none';
};

export const contentTypeToHeader = (contentType: ContentType): KVPairs => {
  if (contentType === 'json') {
    return { key: 'Content-Type', value: 'application/json' };
  }
  if (contentType === 'form-url') {
    return {
      key: 'Content-Type',
      value: 'application/x-www-form-urlencoded',
    };
  }

  return { key: '', value: '' };
};

export const getContentTypePlaceholder = (contentType: ContentType) => {
  if (contentType === 'json') {
    return 'e.g. { "key": "value" }';
  }

  if (contentType === 'form-url') {
    return 'e.g. key1=val1&key2=val2';
  }

  return '';
};

export const onClickTemplateHandlers: Record<
  ActionBuilderTemplate['type'],
  (action?: Action) => (dispatch: AppDispatch) => void
> = {
  hubspot_create_contact_template: () => dispatch => {
    dispatch(
      setSelectedActionTemplate(hubspotContactCreationTemplateInitialValue),
    );
    dispatch(setSelectedInitialActionName(''));
  },
  shopify_order_status_template: action => dispatch => {
    if (action) {
      dispatch(setSelectedActionTemplate(action as SelectedActionTemplate));
      dispatch(setSelectedInitialActionName(action.action_name));
      return;
    }
    dispatch(setSelectedActionTemplate({ ...shopifyTemplateInitialValue }));
    dispatch(setSelectedInitialActionName(''));
  },
};

export const urlToParamsKVPairs = (value: string) => {
  const values = value.split('&');
  const kvPairs: KVPairs[] = [];

  values.forEach(value => {
    const [key, ...v] = value.split('=');

    kvPairs.push({ key, value: v?.join('=') });
  });

  return kvPairs;
};

export const settingsSchema = Yup.object().shape({
  authorizationConfigs: Yup.array()
    .of(
      Yup.object().shape({
        key: Yup.string().trim().min(1).required(),
        value: Yup.string().trim().min(1).required(),
      }),
    )
    .test(validateUniqueKey),
});

export type FlatCv = ContextVariable | DynamicListOption;

const flattenCvsWithFormattedName = (
  cvs: FlatCv[],
  option: FlatCv,
): FlatCv[] => {
  return flattenCvs(cvs).map(cv => ({
    ...cv,
    context_variable_name: `${option.context_variable_name}.${cv.context_variable_name}`,
  }));
};

export const getOutputCvIdsFromBrowserAgent = (
  outputContextVariables: BrowserAgentOutputContextVariables | undefined,
): string[] => {
  if (!outputContextVariables) {
    return [];
  }

  if ('kv_list' in outputContextVariables) {
    const kvList = outputContextVariables.kv_list;
    if (Array.isArray(kvList)) {
      return kvList
        .map(({ value }) => value)
        .filter((id): id is string => Boolean(id));
    }
    return [];
  }

  return Object.values(outputContextVariables).filter(Boolean);
};

export const flattenCvs = (cvs: FlatCv[]): FlatCv[] => {
  return cvs.flatMap(option => {
    if ('configuration_fields' in option && hasDynamicListChildren(option)) {
      if (
        !option.configuration_fields?.dynamic_list_config?.context_variables
      ) {
        throw new Error('Dynamic list config missing context variables');
      }
      return flattenCvsWithFormattedName(
        option.configuration_fields?.dynamic_list_config?.context_variables,
        option,
      );
    }

    if ('options' in option && option.options?.length) {
      return flattenCvsWithFormattedName(option.options, option);
    }

    return option;
  });
};

export const getVariablesFromAction = (
  action: Action,
  contextVariables: ContextVariable[],
): {
  dynamicListOutputVariables?: ContextVariable[];
  inputVariables: ContextVariable[];
  outputVariables: ContextVariable[];
} => {
  const flatCvs = flattenCvs(contextVariables);

  if (isBrowserAgentActionFields(action.action_fields)) {
    const outputContextVariables = normalizeBrowserAgentOutputContextVariables(
      action.action_fields.output_context_variables,
    );
    const outputVariables = Object.values(outputContextVariables)
      .map(cvId =>
        contextVariables.find(({ context_variable_id: id }) => id === cvId),
      )
      .filter((param): param is ContextVariable => param !== undefined);

    return {
      dynamicListOutputVariables: outputVariables.filter(cv =>
        isDynamicListType(cv.context_variable_type),
      ),
      inputVariables: [],
      outputVariables: outputVariables.filter(
        cv => !isDynamicListType(cv.context_variable_type),
      ),
    };
  }

  if (areActionBuilderFields(action.action_fields)) {
    const selectedAction = actionToSelectedActionDetail({
      action,
      contextVariables,
    });

    if (!isApiActionFields(selectedAction.fields)) {
      return { inputVariables: [], outputVariables: [] };
    }

    const { body, headers, jmespathQuery, params, url } = selectedAction.fields;
    const nonUnique = [
      ...params.flatMap(({ value }) => {
        const variableIDs = extractCvIdsFromFieldValue(value);
        return flatCvs.filter(({ context_variable_id: id }) =>
          variableIDs?.includes(id ?? ''),
        );
      }),
      ...headers.flatMap(({ value }) => {
        const variableIDs = extractCvIdsFromFieldValue(value);
        return flatCvs.filter(({ context_variable_id: id }) =>
          variableIDs?.includes(id ?? ''),
        );
      }),
      ...(extractCvIdsFromFieldValue(JSON.stringify(body))?.map(variableId =>
        flatCvs.find(({ context_variable_id: id }) => id === variableId),
      ) ?? []),
      ...(extractCvIdsFromFieldValue(url)?.map(variableId =>
        flatCvs.find(({ context_variable_id: id }) => id === variableId),
      ) ?? []),
      ...(extractCvIdsFromFieldValue(jmespathQuery)?.map(variableId =>
        flatCvs.find(({ context_variable_id: id }) => id === variableId),
      ) ?? []),
    ].filter((param): param is ContextVariable => param !== undefined);
    const inputVariables = Array.from(new Set(nonUnique));
    const outputVariables = selectedAction.outputValues
      .map(outputValue =>
        contextVariables.find(
          ({ context_variable_id: id }) => id === outputValue.value,
        ),
      )
      .filter((param): param is ContextVariable => param !== undefined);

    return {
      dynamicListOutputVariables: outputVariables.filter(cv =>
        isDynamicListType(cv.context_variable_type),
      ),
      inputVariables,
      outputVariables: outputVariables.filter(
        cv => !isDynamicListType(cv.context_variable_type),
      ),
    };
  }

  if (areShopifyFields(action.action_fields)) {
    const { customer_email, customer_id, output_variable } =
      action.action_fields;
    const cvFromEmail = extractCvIdsFromFieldValue(customer_email);
    const cvFromId = extractCvIdsFromFieldValue(customer_id);

    const nonUnique = [...cvFromEmail, ...cvFromId]
      .map(cvId => flatCvs.find(({ context_variable_id: id }) => cvId === id))
      .filter((param): param is ContextVariable => param !== undefined);

    const outputVariables = [output_variable]
      .map(outputValue =>
        contextVariables.find(
          ({ context_variable_id: id }) => id === outputValue,
        ),
      )
      .filter((param): param is ContextVariable => param !== undefined);

    const inputVariables = Array.from(new Set(nonUnique));

    return { inputVariables, outputVariables };
  }

  if (areSnowflakeFields(action.action_fields)) {
    const { output_fields, query_condition } = action.action_fields;
    const cvFromQueryCondition = extractCvIdsFromFieldValue(query_condition);

    const nonUnique = cvFromQueryCondition
      .map(cvId => flatCvs.find(({ context_variable_id: id }) => cvId === id))
      .filter((param): param is ContextVariable => param !== undefined);

    const outputVariables =
      output_fields?.kv_list
        .map(outputValue =>
          contextVariables.find(
            ({ context_variable_id: id }) => id === outputValue.value,
          ),
        )
        .filter((param): param is ContextVariable => param !== undefined) ?? [];

    const inputVariables = Array.from(new Set(nonUnique));

    return { inputVariables, outputVariables };
  }

  return { inputVariables: [], outputVariables: [] };
};

const transformIteratee = (
  acc: Record<string, string>,
  nameValObj: KeyValuePairsDict,
) => {
  const { name, value } = nameValObj;
  if (name && value) {
    acc[name] = value;
  }
  return acc;
};

const buildBaseHeaders = ({
  headers = [],
  idTestValueDict,
}: {
  headers?: KeyValuePairsDict[] | null;
  idTestValueDict?: Record<string, string>;
}) => {
  return flow(
    mapValues((v: string) => replaceCVWithTestValues(v, idTestValueDict)),
    // Don't want any stupid Content-Type vs content-type problems.
    mapKeys((k: string) => k.toLocaleLowerCase()),
  )(headers?.reduce(transformIteratee, {}));
};

const replaceCVWithTestValues = (
  value: string,
  idTestValueDict: Record<string, string> = {},
) => {
  if (isEmpty(idTestValueDict)) {
    return value;
  }
  let retVal = value;
  extractCvIdsFromFieldValue(value)?.forEach(id => {
    const testValue = idTestValueDict[id];
    retVal = retVal.replace(`{{${id}}}`, testValue);
  });

  return retVal;
};

export const replaceCVWithTestValuesForJmespathQuery = (
  value: string,
  idTestValueDict: Partial<Record<string, string>>,
) => {
  let retVal = value;
  extractCvIdsFromFieldValue(value)?.forEach(id => {
    const testValue = idTestValueDict[id];
    retVal = retVal.replace(`{{${id}}}`, testValue ?? '');
  });

  return retVal;
};

// NO encodeUriComponent on purpose! We're going to combine this into the request body
// and have Cloudflare finagle it as-is.
const buildQueryParameters = ({
  idTestValueDict,
  queryParameterArr = [],
}: {
  idTestValueDict?: Record<string, string>;
  queryParameterArr?: KeyValuePairsDict[] | null;
}): Record<string, string> => {
  const queryParameters: Record<string, string> = mapValues(
    (v: string) => replaceCVWithTestValues(v, idTestValueDict),
    queryParameterArr?.reduce(transformIteratee, {}),
  );

  return queryParameters;
};

const buildBaseBodyParameters = ({
  bodyParameters,
  idTestValueDict,
}: {
  bodyParameters?: string | null;
  idTestValueDict?: Record<string, string>;
}): string => {
  if (!bodyParameters) {
    return '';
  }

  let _bodyParameters = bodyParameters;
  if (idTestValueDict) {
    extractCvIdsFromFieldValue(_bodyParameters)?.forEach(id => {
      const testValue = idTestValueDict[id];
      _bodyParameters = _bodyParameters.replace(`{{${id}}}`, testValue);
    });
  }

  return _bodyParameters;
};

const buildAuthorizationHeader = ({
  authorizationConfigs,
  authorizationId,
}: {
  authorizationConfigs: Array<AuthorizationConfig>;
  authorizationId?: string | null;
}): Record<string, string> => {
  if (authorizationId) {
    const authorizationConfigObject = authorizationConfigs.find(
      ({ authorization_id }) => authorization_id === authorizationId,
    );

    if (authorizationConfigObject) {
      return {
        authorization: `${capitalize(
          authorizationConfigObject.authorization_type,
        )} ${authorizationConfigObject.authorization_fields.api_token}`,
      };
    }
  }
  return {};
};

export const buildRequestBodyForIntegrationTestCall = ({
  authorizationConfigs,
  authorizationId,
  authorizationIntegration,
  authorizationType,
  bodyParameters,
  connectorId,
  conversationOauthClientId,
  headers,
  idTestValueDict,
  method,
  oauthConnectorId,
  oauthScope,
  queryParameters,
  url,
}: {
  authorizationConfigs: Array<AuthorizationConfig>;
  authorizationId?: string | null;
  authorizationIntegration?: string | null;
  authorizationType?: string | null;
  bodyParameters?: string | null;
  connectorId?: string | null;
  conversationOauthClientId?: string | null;
  headers?: KeyValuePairsDict[] | null;
  idTestValueDict?: Record<string, string>;
  method: string;
  oauthConnectorId?: string | null;
  oauthScope?: string | null;
  queryParameters: KeyValuePairsDict[] | null;
  url: string;
}): ProxyTestRequest => {
  const body = buildBaseBodyParameters({
    bodyParameters,
    idTestValueDict,
  });
  let bodyJson: unknown = body;

  if (
    headers?.find(header => header.value.toLowerCase() === 'application/json')
  ) {
    try {
      bodyJson = body ? JSON.parse(body) : null;
    } catch {}
  }

  const header = {
    ...buildBaseHeaders({ headers, idTestValueDict }),
    ...buildAuthorizationHeader({
      authorizationConfigs,
      authorizationId,
    }),
  };
  return {
    authorization_integration: authorizationIntegration,
    authorization_type: authorizationType,
    body: bodyJson,
    connector_id: connectorId,
    conversation_oauth_client_id: conversationOauthClientId,
    headers: header,
    http_method: method.toLocaleUpperCase(),
    oauth_connector_id: oauthConnectorId,
    oauth_scope: oauthScope
      ? replaceCVWithTestValues(oauthScope, idTestValueDict)
      : oauthScope,
    query_parameters: buildQueryParameters({
      idTestValueDict,
      queryParameterArr: queryParameters,
    }),
    url: replaceCVWithTestValues(url, idTestValueDict),
  };
};

export const buildUrlOptions = ({
  authorizationConfigs,
  authorizationId,
  bodyParameters,
  headers,
  idTestValueDict,
  method,
  queryParameters,
  url,
}: {
  authorizationConfigs: Array<AuthorizationConfig>;
  authorizationId?: string | null;
  bodyParameters?: string | null;
  headers?: KeyValuePairsDict[] | null;
  idTestValueDict?: Record<string, string>;
  method: string;
  queryParameters: KeyValuePairsDict[] | null;
  url: string;
}): Record<string, unknown> => {
  const body = pickBy((value: unknown) => !isEmpty(value), {
    _apiUrl: replaceCVWithTestValues(url, idTestValueDict),

    _body: buildBaseBodyParameters({
      bodyParameters,
      idTestValueDict,
    }),

    _headers: {
      ...buildBaseHeaders({ headers, idTestValueDict }),
      ...buildAuthorizationHeader({
        authorizationConfigs,
        authorizationId,
      }),
    },

    _method: method.toLocaleUpperCase(),

    // Ugly, but grouping query params into body to bypass Cloudflare decoding bug with query params.
    _queryParameters: buildQueryParameters({
      idTestValueDict,
      queryParameterArr: queryParameters,
    }),
  });

  const options: Record<string, unknown> = {
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  };

  return options;
};

const isEmbedScriptParam = (cv: ContextVariable): boolean => {
  if (
    cv.is_universal_context_variable &&
    excludeEmbedScriptParamNames.includes(cv.context_variable_name)
  ) {
    return false;
  }
  if (
    cv.is_universal_context_variable &&
    includeEmbedScriptParamNames.includes(cv.context_variable_name)
  ) {
    return true;
  }
  return cv.is_workflow_context || false;
};

export const getWorkflowContextParams = (
  contextVariables: ContextVariable[],
) => {
  return (
    contextVariables
      .filter(cv => cv.is_workflow_context)
      .map(cv => cv.context_variable_name) || []
  );
};

export const getContextEmbedScriptParams = (
  contextVariables: ContextVariable[],
) => {
  return (
    contextVariables
      .filter(cv => isEmbedScriptParam(cv))
      .map(cv => cv.context_variable_name) || []
  );
};

export const getContextVariableNumbers = (
  contextVariables: ContextVariable[],
) => {
  return (
    contextVariables
      .filter(cv => cv.context_variable_type === 'NUMBER')
      .map(cv => cv.context_variable_name) || []
  );
};

export const getWorkflowContextVariables = (
  contextVariables: ContextVariable[],
) => contextVariables.filter(cv => cv.is_workflow_context);

export const isDynamicListKvPairs = (
  data: OutputValues,
): data is DynamicListKVPairs => 'jsonSchema' in data;
